import {
    ensureMicrophonePermission,
    getMicrophoneAccessErrorMessage,
} from "@components/internal-contacts/_utils/requestMicrophoneAccess.utils";
import { AudioRecorder } from "@components/virtual-receptionist/sms/chat/components/AudioRecorder";
import { VoiceMessageRecordingSlot } from "@components/virtual-receptionist/sms/types/update-company-config";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MicIcon from "@mui/icons-material/Mic";
import ReplayIcon from "@mui/icons-material/Replay";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, CircularProgress, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { VoiceMessageSlot } from "../constants/voice-message-slots";
import { voiceMessagesFieldSx } from "../constants/voice-messages-field-sx";
import { useVoiceMessageRecordingMutation } from "../hooks/useVoiceMessageRecordingMutation";

const ORANGE = "#FB8E00";

const AUDIO_RECORDER_LABELS = {
    discardTooltip: "Discard",
    stopTooltip: "Stop",
    confirmTooltip: "Confirm",
    micAccessErrorMessage: "Could not access the microphone.",
};

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type PipelineMode = "text" | "recording" | "upload";

type Props = {
    slotKey: VoiceMessageSlot;
    label: string;
    placeholder: string;
    helperText: string;
    textValue: string;
    onTextChange: (value: string) => void;
    mode: PipelineMode;
    onModeChange: (mode: PipelineMode) => void;
    recordingSlot?: VoiceMessageRecordingSlot;
    companyId: number;
    minRows?: number;
};

export function VoiceMessagePipelineRow({
    slotKey,
    label,
    placeholder,
    helperText,
    textValue,
    onTextChange,
    mode,
    onModeChange,
    recordingSlot,
    companyId,
    minRows = 2,
}: Props) {
    const { showSnackbar } = useGlobalSnackbar();
    const { upload, remove } = useVoiceMessageRecordingMutation();
    const [isRecorderOpen, setIsRecorderOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const hasSavedRecording = recordingSlot?.mode === "recording" && Boolean(recordingSlot.audioUrl);
    const isBusy = upload.isPending || remove.isPending;

    const handleModeChange = async (_event: React.MouseEvent<HTMLElement>, nextMode: PipelineMode | null) => {
        if (!nextMode || nextMode === mode) return;

        if (nextMode === "text") {
            setIsRecorderOpen(false);
            onModeChange("text");
            return;
        }

        if (nextMode === "recording") {
            onModeChange("recording");

            if (!hasSavedRecording) {
                await handleStartRecording();
            }
            return;
        }

        onModeChange("upload");

        if (!hasSavedRecording) {
            handleOpenFilePicker();
        }
    };

    const handleStartRecording = async () => {
        const result = await ensureMicrophonePermission();

        if (result.ok === false) {
            showSnackbar(getMicrophoneAccessErrorMessage(result.reason), "error");
            onModeChange("text");
            return;
        }

        setIsRecorderOpen(true);
    };

    const handleRecordingComplete = async (file: File) => {
        setIsRecorderOpen(false);
        await upload.mutateAsync({ companyId, slotKey, audio: file });
    };

    const handleRecorderCancel = () => {
        setIsRecorderOpen(false);

        if (!hasSavedRecording) {
            onModeChange("text");
        }
    };

    const handleOpenFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (!file) {
            if (!hasSavedRecording) {
                onModeChange("text");
            }
            return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            showSnackbar("Audio file must be 10MB or smaller.", "error");
            if (!hasSavedRecording) {
                onModeChange("text");
            }
            return;
        }

        await upload.mutateAsync({ companyId, slotKey, audio: file });
    };

    const handleDeleteRecording = async () => {
        await remove.mutateAsync({ companyId, slotKey });
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75, gap: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>{label}</Typography>

                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mode}
                    onChange={handleModeChange}
                    disabled={isBusy}
                    sx={{
                        "& .MuiToggleButton-root": {
                            textTransform: "none",
                            fontSize: 12,
                            fontWeight: 600,
                            px: 1.5,
                            py: 0.4,
                        },
                        "& .Mui-selected": {
                            bgcolor: `${ORANGE}1a !important`,
                            color: `${ORANGE} !important`,
                        },
                    }}
                >
                    <ToggleButton value="text">Write text</ToggleButton>
                    <ToggleButton value="recording">Record voice</ToggleButton>
                    <ToggleButton value="upload">Upload audio</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box
                component="input"
                type="file"
                accept="audio/*"
                ref={fileInputRef}
                onChange={handleFileSelected}
                sx={{ display: "none" }}
            />

            {mode === "text" ? (
                <TextField
                    placeholder={placeholder}
                    helperText={helperText}
                    value={textValue}
                    onChange={(event) => onTextChange(event.target.value)}
                    multiline
                    minRows={minRows}
                    fullWidth
                    sx={voiceMessagesFieldSx}
                />
            ) : (
                <Box
                    sx={{
                        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                        borderRadius: "10px",
                        bgcolor: "var(--fx-surface-base, #FFFFFF)",
                        overflow: "hidden",
                    }}
                >
                    {mode === "recording" && isRecorderOpen ? (
                        <AudioRecorder
                            onRecordingComplete={handleRecordingComplete}
                            onCancel={handleRecorderCancel}
                            labels={AUDIO_RECORDER_LABELS}
                        />
                    ) : hasSavedRecording ? (
                        <Stack direction="row" alignItems="center" gap={1} px={1.5} py={1}>
                            <Box
                                component="audio"
                                controls
                                src={recordingSlot?.audioUrl}
                                sx={{ height: 36, flex: 1, minWidth: 0 }}
                            />

                            {isBusy ? (
                                <CircularProgress size={20} sx={{ color: ORANGE }} />
                            ) : (
                                <>
                                    <Tooltip title={mode === "upload" ? "Re-upload" : "Re-record"}>
                                        <IconButton
                                            size="small"
                                            onClick={mode === "upload" ? handleOpenFilePicker : handleStartRecording}
                                            sx={{ color: ORANGE }}
                                        >
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Delete recording">
                                        <IconButton size="small" onClick={handleDeleteRecording} sx={{ color: "#b91c1c" }}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </Stack>
                    ) : mode === "upload" ? (
                        <Box px={1.5} py={1.5} display="flex" justifyContent="center">
                            <Button
                                startIcon={<UploadFileIcon />}
                                onClick={handleOpenFilePicker}
                                disabled={isBusy}
                                sx={{ color: ORANGE, textTransform: "none", fontWeight: 700 }}
                            >
                                Choose an audio file
                            </Button>
                        </Box>
                    ) : (
                        <Box px={1.5} py={1.5} display="flex" justifyContent="center">
                            <Button
                                startIcon={<MicIcon />}
                                onClick={handleStartRecording}
                                disabled={isBusy}
                                sx={{ color: ORANGE, textTransform: "none", fontWeight: 700 }}
                            >
                                Record audio
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {mode !== "text" && (
                <Typography sx={{ fontSize: 12, color: "var(--fx-surface-texttertiary, #94A3B8)", mt: 0.5 }}>
                    {mode === "upload" ? "MP3, WAV, M4A, or OGG — up to 10MB." : helperText}
                </Typography>
            )}
        </Box>
    );
}
